import { FC, memo, ReactElement } from 'react'
import { Link, useRouteMatch } from 'react-router-dom'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { ExternalLinkOutlinedIcon } from '@getgo/chameleon-icons/react'
import { NavigationDrawerLink, TextBadge, Tooltip } from '@getgo/chameleon-web-react-wrapper'
import { makeStyles } from '@material-ui/core'
import mixpanel from 'mixpanel-browser'

import { mixpanelEventNames } from 'utils'

const useStyles = makeStyles(() => ({
  navDrawerLink: {
    width: '100%',
  },
  badge: {
    backgroundColor: tokens.surfaceColorBrandDefault,
    color: tokens.iconColorInverted,
  },
  selectedBadge: {
    backgroundColor: tokens.backgroundColorSecondary,
    color: tokens.typeColorDefault,
  },
}))

interface ListItemLinkProps {
  activeIcon?: ReactElement
  inactiveIcon?: ReactElement
  badge?: string
  badgeTooltip?: string
  primary: string
  urlKey: string
  to: string
  onClick?: () => void
}

const ListItemLink: FC<ListItemLinkProps | any> = ({
  activeIcon,
  external,
  inactiveIcon,
  badge,
  badgeTooltip,
  onClick,
  urlKey,
  primary,
  startIcon,
  to,
  ...otherProps
}) => {
  const classes = useStyles()
  const match = useRouteMatch({
    path: to,
  })

  const resolveBadge = () => {
    if (badge) {
      return (
        <div>
          <TextBadge id="badge-icon" className={match ? classes.selectedBadge : classes.badge}>
            {badge}
          </TextBadge>
          {badgeTooltip && <Tooltip triggerId="badge-icon">{badgeTooltip}</Tooltip>}
        </div>
      )
    }
    if (external) {
      return <ExternalLinkOutlinedIcon data-testid="external-link-icon" />
    }
    return null
  }

  const ItemLink = () => (
    <NavigationDrawerLink
      className={classes.navDrawerLink}
      onClick={() => {
        if (onClick) {
          onClick()
          mixpanel.track(mixpanelEventNames.SIDEBAR, {
            Action: `Navigated to ${urlKey}`,
          })
        }
      }}
      start={startIcon || (inactiveIcon && match ? activeIcon : inactiveIcon)}
      selected={!!match}
      end={resolveBadge()}
      {...otherProps}
    >
      {primary}
    </NavigationDrawerLink>
  )

  return (
    <>
      {external ? (
        <a href={to} rel="noopener noreferrer" target="_blank" style={{ textDecoration: 'none' }}>
          <ItemLink />
        </a>
      ) : (
        to && (
          <Link to={to} href={to} style={{ textDecoration: 'none' }} className={classes.navDrawerLink}>
            <ItemLink />
          </Link>
        )
      )}
    </>
  )
}

export default memo(ListItemLink)
